import { ArrowBackOutlined, CloseOutlined } from '@mui/icons-material'
import {
    Box,
    Dialog,
    DialogContent,
    DialogTitle,
    Icon,
    Typography,
} from '@mui/material'
import MapContainer from './MapContainer'
import React, { useMemo } from 'react'
import MapContainerPolygon from './MapContainerPolygon'
import { useTranslation } from 'react-i18next'

interface LocationViewProps {
    isMapOpen: boolean
    onClickBackInMap: () => void
    geoJSON: GeoJSON.FeatureCollection
}

const LocationView: React.FC<LocationViewProps> = (props) => {
    const { t } = useTranslation()
    const { isMapOpen, onClickBackInMap, geoJSON } = props

    const { selectedMapType, range, points, selectedPosition, zoom } =
        useMemo(() => {
            if (!geoJSON) {
                return {
                    selectedMapType: 'Circle',
                    range: 0,
                    points: [],
                    selectedPosition: null,
                    zoom: 10,
                }
            }

            /**
             * We always have only one feature in our geoJSON data for a location. Hence index 0.
             */
            const feature = geoJSON.features[0]

            const markPoint = (
                geoJSON.features?.find((t) => t.geometry.type === 'Point')
                    ?.geometry as GeoJSON.Point
            )?.coordinates

            let updatselectedPosition = null
            if (markPoint) {
                updatselectedPosition = {
                    lat: markPoint[1],
                    lng: markPoint[0],
                }
            } else {
                const markPoint = geoJSON.features?.find(
                    (t) => t.geometry.type === 'Polygon',
                )?.properties?.coordinates
                /**
                 * Below 74 and 40 is a temporary arrangement. Should be removed later.
                 */
                updatselectedPosition = {
                    lat: markPoint?.[1] || 41,
                    lng: markPoint?.[0] || -75,
                }
            }

            const radius = feature?.properties?.radius

            const mapType = feature?.properties?.mapType ?? 'Polygon'
            const isPolygon = mapType === 'Polygon'

            const settedZoom = feature?.properties?.zoom

            const polygonCoordinates = isPolygon
                ? (feature?.geometry as GeoJSON.Polygon)?.coordinates
                : [[]]

            return {
                selectedMapType: mapType,
                range: radius,
                points: polygonCoordinates[0]?.slice(
                    0,
                    polygonCoordinates[0]?.length - 1,
                ),
                selectedPosition: updatselectedPosition,
                zoom: settedZoom ?? 18,
            }
        }, [geoJSON])

    return (
        <Dialog
            open={isMapOpen}
            onClose={onClickBackInMap}
            scroll="paper"
            PaperProps={{
                sx: {
                    width: '100%',
                    maxWidth: '920px!important',
                },
            }}
            aria-labelledby="scroll-dialog-title"
            aria-describedby="scroll-dialog-description"
            sx={{ padding: '10px 30px' }}
        >
            <Box>
                <DialogTitle id="scroll-dialog-title">
                    <Box
                        sx={{ marginBottom: '5px' }}
                        display="flex"
                        alignItems="center"
                    >
                        <Icon
                            onClick={onClickBackInMap}
                            sx={{
                                marginRight: '10px',
                                cursor: 'pointer',
                            }}
                            fontSize="small"
                            aria-label={t('back')}
                            title={t('back')}
                        >
                            <ArrowBackOutlined
                                sx={{
                                    color: '#000',
                                    marginTop: '-2px',
                                }}
                            />
                        </Icon>
                        <Typography
                            id="scroll-dialog-title"
                            sx={{
                                color: '#000',
                                fontSize: '18px',
                                fontWeight: 600,
                            }}
                        >
                            {t('locationDetails')}
                        </Typography>
                        <Icon
                            onClick={onClickBackInMap}
                            sx={{
                                marginLeft: 'auto',
                                cursor: 'pointer',
                            }}
                            fontSize="small"
                            aria-label={t('close')}
                            title={t('close')}
                        >
                            <CloseOutlined
                                sx={{
                                    color: '#000',
                                    marginTop: '-2px',
                                }}
                            />
                        </Icon>
                    </Box>
                </DialogTitle>
                <DialogContent
                    sx={{
                        height: 'calc(100vh - 250px)',
                    }}
                >
                    <Box sx={{ height: '100%' }}>
                        {selectedMapType === 'Circle' ? (
                            <MapContainer
                                markerPosition={selectedPosition}
                                radius={range}
                                zoom={zoom}
                                editable={false}
                            />
                        ) : (
                            <MapContainerPolygon
                                markerPosition={selectedPosition}
                                polygon={points}
                                editable={false}
                                zoom={zoom}
                            />
                        )}
                    </Box>
                </DialogContent>
            </Box>
        </Dialog>
    )
}

export default LocationView
